import React from 'react';
import { useTranslation } from 'react-i18next';
import { Task } from 'graphql-common';
import Modal from '@lib/components/Modal/Modal';
import { ModalMode } from '@lib/components/Modal/enums';
import { TabBarItem } from '@lib/components/TabBar/constants';
import { FetchIdsResponse } from '@lib/utils/getAllTableItemsIds';
import getBucketPeriodLabel from '@lib/components/TaskCompletions/utils/getBucketPeriodLabel';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import useTaskCompletionsActions from './utils/useTaskCompletionsActions';
import useTaskCompletionsScope from './utils/useTaskCompletionsScope';
import TaskCompletionDetails from './CompletionDetails/TaskCompletionDetails';
import TaskCompletionsList from './CompletionList/TaskCompletionsList';
import { TaskCompletionsModalActions, TaskCompletionsModalUrls } from './types';

type Props = {
  task: Task | null;
  bucketId: string | null;
  // Lists only the completions this intervention covers (Intervention view).
  interventionId?: string | null;
  onClose: () => void;
  // Opens straight to this completion's detail view instead of the list.
  initialCompletionId?: string | null;
  // Opens straight into the bulk-compare view for a pre-selected set of
  // completions instead of the list (e.g. a bulk "Compare completions"
  // action on another screen, such as admin's Task Reporting).
  initialBulkCompareIds?: string[] | null;
  /**
   * Drops the back arrow, so the completions list stays unreachable. For a
   * caller that opens this modal on one completion from a context that has no
   * list behind it — e.g. the "view" row action inside
   * `TaskCompletionsTableMultiselect`, where "back" would otherwise surface a
   * list the user never came from, along with its row actions menu (whose
   * "Create intervention" navigates away from the form they are filling in).
   * Pair with `initialCompletionId` or `initialBulkCompareIds`.
   */
  detailOnly?: boolean;
  // Keeps the modal on the list: no row click, no "Completion details" menu
  // item and no "Compare completions" bulk action (nor, with no other bulk
  // action left, row checkboxes) — for a host whose users shouldn't open
  // completion details (the user app).
  hideCompletionDetails?: boolean;
  /**
   * Overrides which completions the detail view's prev/next and compare mode
   * browse. Without it they cover the whole group; a host showing a filtered
   * list (admin's Task Reporting) passes its own so navigation stays inside
   * the set the user is looking at.
   */
  fetchIds?: () => Promise<FetchIdsResponse<unknown>>;
  // Defaults to `task-completions`; a host that only ever opens the detail
  // view can title it for what it actually shows.
  caption?: string;
  renderAfterCaption?: React.ReactNode;
  /**
   * Leads the list and detail headers with a chip naming the task, for a host
   * with no timeline bucket to show a period for (e.g. Intervention view /
   * form, where the task is the context the user came from).
   */
  showTaskChip?: boolean;
  // Keep both referentially stable (module constant / useMemo) — they feed
  // memoized table columns and menus.
  urls: TaskCompletionsModalUrls;
  actions?: TaskCompletionsModalActions;
};

export default function TaskCompletionsModal({
  task,
  bucketId,
  interventionId,
  onClose,
  initialCompletionId,
  initialBulkCompareIds,
  detailOnly,
  hideCompletionDetails,
  fetchIds,
  caption,
  renderAfterCaption,
  showTaskChip,
  urls,
  actions,
}: Props) {
  const { t, i18n } = useTranslation();
  // A timeline cell opens its own bucket's completions, an intervention the
  // ones it covers — the list, tab counters, media and detail prev/next all
  // stay inside that scope.
  const {
    completionFilters,
    formAnswerFilters,
    loading: scopeLoading,
  } = useTaskCompletionsScope({
    groupKey: task?.groupKey,
    bucketId,
    interventionId,
  });
  const {
    activeTab,
    onTabChange,
    tabs,
    tableColumns,
    tableData,
    tableLoading,
    tableFirstLoading,
    tableError,
    totalCount,
    paginationParams,
    setPaginationParams,
    actionsMenu,
    tableActionsForSelected,
    hasBulkActions,
    useBulkSelectResult,
    selectedCompletionId,
    onChangeSelectedCompletionId,
    onBackToList,
    orderingField,
    orderingDirection,
    bulkCompareIds,
    onViewCompletion,
  } = useTaskCompletionsActions({
    // Held back until the scope is known, so nothing is fetched unscoped.
    task: scopeLoading ? null : task,
    completionFilters,
    formAnswerFilters,
    initialCompletionId,
    initialBulkCompareIds,
    detailOnly,
    hideCompletionDetails,
    onClose,
    urls,
    actions,
  });

  const periodLabel = getBucketPeriodLabel(bucketId, i18n.language);

  let modalChildren: React.ReactElement | undefined;
  if (task && scopeLoading) {
    modalChildren = <CircleLoader />;
  } else if (task && bulkCompareIds) {
    modalChildren = (
      <TaskCompletionDetails
        task={task}
        groupKey={task.groupKey}
        onChangeCompletionId={onChangeSelectedCompletionId}
        bulkCompareIds={bulkCompareIds}
        orderingField={orderingField}
        orderingDirection={orderingDirection}
        onBackToList={onBackToList}
        baseFilters={completionFilters}
        fetchIds={fetchIds}
        showTaskChip={showTaskChip}
        urls={urls}
      />
    );
  } else if (task && selectedCompletionId) {
    modalChildren = (
      <TaskCompletionDetails
        task={task}
        groupKey={task.groupKey}
        completionId={selectedCompletionId}
        onChangeCompletionId={onChangeSelectedCompletionId}
        orderingField={orderingField}
        orderingDirection={orderingDirection}
        baseFilters={completionFilters}
        fetchIds={fetchIds}
        showTaskChip={showTaskChip}
        urls={urls}
      />
    );
  } else if (task) {
    modalChildren = (
      <TaskCompletionsList
        task={task}
        periodLabel={periodLabel}
        formAnswerFilters={formAnswerFilters}
        showTaskChip={showTaskChip}
        activeTab={activeTab}
        onTabChange={onTabChange}
        tabs={tabs as TabBarItem[]}
        tableColumns={tableColumns}
        tableData={tableData}
        tableLoading={tableLoading}
        tableFirstLoading={tableFirstLoading}
        tableError={tableError}
        totalCount={totalCount}
        paginationParams={paginationParams}
        setPaginationParams={setPaginationParams}
        actionsMenu={actionsMenu}
        actionsForSelected={tableActionsForSelected}
        hasSelectColumn={hasBulkActions}
        useBulkSelectResult={useBulkSelectResult}
        onRowClick={onViewCompletion}
        urls={urls}
      />
    );
  }

  return (
    <Modal
      isOpen={!!task}
      onClose={onClose}
      onBack={
        !detailOnly && (selectedCompletionId || bulkCompareIds)
          ? onBackToList
          : undefined
      }
      caption={caption || t('task-completions')}
      renderAfterCaption={renderAfterCaption}
      mode={ModalMode.FullScreen}
    >
      {modalChildren}
    </Modal>
  );
}
